import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, ShoppingBag, Store, User, LogOut, Menu, Globe, LayoutDashboard, MessageCircle, Bell } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useApp } from "@/context/AppContext";
import api from "@/lib/api";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

export function Header() {
  const { user, logout } = useAuth();
  const { t, lang, toggleLang, cart, brand } = useApp();
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [unread, setUnread] = useState(0);

  useEffect(() => {
    if (!user) { setUnread(0); return; }
    let live = true;
    const tick = () => api.get("/chat/unread").then(({ data }) => { if (live) setUnread(data.unread); }).catch(() => {});
    tick();
    const iv = setInterval(tick, 10000);
    return () => { live = false; clearInterval(iv); };
  }, [user]);

  const submit = (e) => { e.preventDefault(); navigate(`/belanja?q=${encodeURIComponent(q)}`); };

  return (
    <header className="sticky top-0 z-50 backdrop-blur-md bg-rl-bg/90 border-b border-rl-border">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">
        <div className="flex items-center gap-4 h-16">
          <Link to="/" data-testid="brand-logo" className="flex items-center gap-2 shrink-0">
            <span className="font-serif text-2xl font-bold tracking-tight text-rl-ink">{brand?.brand_name || "RELOVA"}</span>
            <span className="hidden sm:inline text-[10px] font-semibold text-rl-muted tracking-widest uppercase border-l border-rl-border pl-2">Preloved</span>
          </Link>

          <form onSubmit={submit} className="flex-1 max-w-xl hidden md:flex items-center bg-white rounded-full border border-rl-border px-4 h-10 focus-within:border-rl-primary transition-colors">
            <Search className="w-4 h-4 text-rl-muted" />
            <input data-testid="search-input" value={q} onChange={(e) => setQ(e.target.value)}
              placeholder={t("search_ph")} className="flex-1 bg-transparent outline-none text-sm px-2" />
          </form>

          <div className="flex items-center gap-1 sm:gap-2 ml-auto">
            <button data-testid="lang-toggle" onClick={toggleLang}
              className="flex items-center gap-1 text-sm font-semibold text-rl-ink2 hover:text-rl-primary px-2 py-1 rounded-md transition-colors">
              <Globe className="w-4 h-4" />{lang.toUpperCase()}
            </button>

            <Link to="/checkout" data-testid="cart-link" className="relative p-2 rounded-md hover:bg-rl-bg2 transition-colors">
              <ShoppingBag className="w-5 h-5 text-rl-ink" />
              {cart.length > 0 && <span className="absolute -top-0.5 -right-0.5 bg-rl-primary text-white text-[10px] font-bold rounded-full w-4 h-4 grid place-items-center">{cart.length}</span>}
            </Link>

            {user && (
              <Link to="/chat" data-testid="chat-bell" className="relative p-2 rounded-md hover:bg-rl-bg2 transition-colors">
                <Bell className="w-5 h-5 text-rl-ink" />
                {unread > 0 && <span data-testid="chat-unread-badge" className="absolute -top-0.5 -right-0.5 bg-rl-primary text-white text-[10px] font-bold rounded-full min-w-[16px] h-4 px-1 grid place-items-center">{unread}</span>}
              </Link>
            )}

            {Boolean(user && !user.has_store && user.role !== "seller" && user.role !== "admin") && (
              <Link to="/buka-toko" data-testid="header-sell-cta"
                className="hidden sm:flex items-center gap-1.5 rounded-full bg-rl-secondary hover:bg-[#2E473D] text-white text-xs font-bold px-4 py-2 transition-colors">
                <Store className="w-4 h-4" />{t("nav_sell")}
              </Link>
            )}

            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button data-testid="user-menu" className="p-2 rounded-full bg-rl-bg2 hover:bg-rl-border transition-colors">
                    <User className="w-5 h-5 text-rl-ink" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <div className="px-2 py-1.5 text-sm font-semibold truncate">{user.name}</div>
                  <div className="px-2 pb-1.5 text-xs text-rl-muted truncate">{user.email}</div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem data-testid="menu-orders" onClick={() => navigate("/pesanan")}>
                    <ShoppingBag className="w-4 h-4 mr-2" />{t("nav_orders")}
                  </DropdownMenuItem>
                  <DropdownMenuItem data-testid="menu-chat" onClick={() => navigate("/chat")}>
                    <MessageCircle className="w-4 h-4 mr-2" />Pesan
                  </DropdownMenuItem>
                  {(user.role === "seller" || user.role === "admin") && (
                    <DropdownMenuItem data-testid="menu-dashboard" onClick={() => navigate("/seller")}>
                      <Store className="w-4 h-4 mr-2" />{t("nav_dashboard")}
                    </DropdownMenuItem>
                  )}
                  {user.role === "admin" && (
                    <DropdownMenuItem data-testid="menu-admin" onClick={() => navigate("/admin")}>
                      <LayoutDashboard className="w-4 h-4 mr-2" />{t("nav_admin")}
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem data-testid="menu-logout" onClick={async () => { await logout(); navigate("/"); }}>
                    <LogOut className="w-4 h-4 mr-2" />{t("nav_logout")}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <div className="flex items-center gap-1">
                <Link to="/login" data-testid="header-login" className="text-sm font-semibold text-rl-ink2 hover:text-rl-primary px-3 py-1.5">{t("nav_login")}</Link>
                <Link to="/register" data-testid="header-register" className="text-sm font-bold text-white bg-rl-primary hover:bg-rl-primaryHover rounded-full px-4 py-1.5 transition-colors">{t("nav_register")}</Link>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}

export function Footer() {
  const { brand } = useApp();
  return (
    <footer className="mt-20 border-t border-rl-border bg-rl-bg2">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-12 grid md:grid-cols-4 gap-8">
        <div>
          <div className="font-serif text-2xl font-bold text-rl-ink">{brand?.brand_name || "RELOVA"}</div>
          <p className="mt-2 text-sm text-rl-ink2 max-w-xs">{brand?.tagline || "Barang Bagus Layak Dicintai Lagi."}</p>
          <p className="mt-4 text-xs text-rl-muted">{brand?.legal_company_name}</p>
        </div>
        <div>
          <h4 className="text-xs uppercase tracking-wider font-semibold text-rl-muted mb-3">Marketplace</h4>
          <ul className="space-y-2 text-sm text-rl-ink2">
            <li><Link to="/belanja" className="hover:text-rl-primary">Belanja Preloved</Link></li>
            <li><Link to="/buka-toko" className="hover:text-rl-primary">Buka Toko</Link></li>
            <li><Link to="/pesanan" className="hover:text-rl-primary">Pesanan Saya</Link></li>
          </ul>
        </div>
        <div>
          <h4 className="text-xs uppercase tracking-wider font-semibold text-rl-muted mb-3">Bantuan</h4>
          <ul className="space-y-2 text-sm text-rl-ink2">
            <li>{brand?.support_email}</li>
          </ul>
        </div>
        <div>
          <h4 className="text-xs uppercase tracking-wider font-semibold text-rl-muted mb-3">Sosial</h4>
          <ul className="space-y-2 text-sm text-rl-ink2">
            <li>{brand?.social?.instagram}</li>
            <li>{brand?.social?.tiktok}</li>
          </ul>
        </div>
      </div>
      <div className="border-t border-rl-border py-4 text-center text-xs text-rl-muted">
        © 2026 {brand?.legal_company_name || "PT RAKSA MADHA KARYA"}. All rights reserved.
      </div>
    </footer>
  );
}

export function Layout({ children }) {
  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-1">{children}</main>
      <Footer />
    </div>
  );
}
